import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { productContractAPI } from "@/lib/api";
import ProductPicker from "./ProductPicker";
import { toast } from "sonner";
import { DateField } from "@/components/ui/date-field";

function formatEur(v) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
}

const emptyItem = { product_name: "", label: "", unit_price: "", quantity: 1 };
const defaultForm = { items: [{ ...emptyItem }], start_date: "", end_date: "", next_billing_date: "", subscription_mode: "Annuel", payment_mode: "", is_active: true };

export default function ContractForm({ open, onOpenChange, clientId, contractId, contractData, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ ...defaultForm, items: [{ ...emptyItem }] });
  const isEditing = !!(contractId || contractData);

  useEffect(() => {
    if (!open) return;
    if (contractData) {
      const items = contractData.items?.length
        ? contractData.items.map(it => ({ ...it }))
        : [{ product_name: contractData.product_name || "", label: contractData.label || "", unit_price: contractData.client_price || 0, quantity: 1 }];
      setForm({ ...contractData, items });
      setLoading(false);
    } else if (contractId) {
      setLoading(true);
      productContractAPI.get(contractId)
        .then(r => {
          const c = r.data;
          const items = c.items?.length
            ? c.items.map(it => ({ ...it }))
            : [{ product_name: c.product_name || "", label: c.label || "", unit_price: c.client_price || 0, quantity: 1 }];
          setForm({ ...c, items });
        })
        .catch(() => toast.error("Erreur chargement du contrat"))
        .finally(() => setLoading(false));
    } else {
      setForm({ ...defaultForm, items: [{ ...emptyItem }] });
      setLoading(false);
    }
  }, [open, contractId, contractData]);

  const save = async () => {
    try {
      const items = form.items.filter(it => it.product_name || it.label || it.unit_price);
      const data = { ...form, items, client_id: clientId || form.client_id };
      if (items.length > 0) {
        data.product_name = items[0].product_name;
        data.label = items.length > 1 ? `${items.length} lignes` : items[0].label || "";
      }
      const id = contractId || contractData?.id;
      if (id) {
        await productContractAPI.update(id, data);
        toast.success("Contrat mis à jour");
      } else {
        await productContractAPI.create(data);
        toast.success("Contrat créé");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setItem = (idx, k, v) => setForm(prev => {
    const items = [...prev.items];
    items[idx] = { ...items[idx], [k]: v };
    return { ...prev, items };
  });
  const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, { ...emptyItem, _key: crypto.randomUUID() }] }));
  const removeItem = (idx) => setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  const totalItems = form.items.reduce((s, it) => s + (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 1), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="contract-dialog">
        <DialogHeader><DialogTitle>{isEditing ? "Modifier le contrat" : "Nouveau contrat"}</DialogTitle></DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin h-6 w-6 text-slate-400" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Prochaine facturation</Label>
                <DateField value={form.next_billing_date || ""} onChange={e => setField("next_billing_date", e.target.value)} data-testid="contract-next-billing" />
              </div>
              <div>
                <Label>Periodicite</Label>
                <Select value={form.subscription_mode} onValueChange={v => setField("subscription_mode", v)}>
                  <SelectTrigger data-testid="contract-period-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Mensuel">Mensuel</SelectItem>
                    <SelectItem value="Trimestriel">Trimestriel</SelectItem>
                    <SelectItem value="Semestriel">Semestriel</SelectItem>
                    <SelectItem value="Annuel">Annuel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Mode de règlement</Label>
                <Select value={form.payment_mode || ""} onValueChange={v => setField("payment_mode", v)}>
                  <SelectTrigger data-testid="contract-payment-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Virement">Virement</SelectItem>
                    <SelectItem value="Cheque">Chèque</SelectItem>
                    <SelectItem value="Prélèvement">Prélèvement</SelectItem>
                    <SelectItem value="Carte">Carte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2 pb-1">
                <Switch checked={form.is_active !== false} onCheckedChange={v => setField("is_active", v)} data-testid="contract-active-switch" />
                <Label className="text-sm">{form.is_active !== false ? "Actif" : "Inactif"}</Label>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold">Lignes du contrat</Label>
                <Button type="button" variant="outline" size="sm" className="text-xs h-7" onClick={addItem} data-testid="add-item-btn">
                  <Plus size={14} className="mr-1" /> Ajouter une ligne
                </Button>
              </div>
              <div className="space-y-2">
                {form.items.map((item, idx) => (
                  <div key={item._key || `contract-${idx}`} className="flex gap-2 items-start p-2 bg-slate-50 rounded-md border border-slate-200" data-testid={`contract-item-${idx}`}>
                    <div className="flex-1 space-y-1.5">
                      <div>
                        <Label className="text-[11px] text-slate-500">Produit</Label>
                        <ProductPicker
                          value={item.product_name}
                          onSelect={(p) => {
                            setItem(idx, "product_name", p.name);
                            const price = p.unit_price || p.sale_price_ht;
                            if (price) setItem(idx, "unit_price", price);
                          }}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <div>
                          <Label className="text-[11px] text-slate-500">Label / Description</Label>
                          <Input placeholder="Description" value={item.label || ""} onChange={e => setItem(idx, "label", e.target.value)} className="h-8 text-sm" data-testid={`item-label-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[11px] text-slate-500">Prix unitaire HT</Label>
                          <Input type="number" step="0.01" placeholder="0.00" value={item.unit_price} onChange={e => setItem(idx, "unit_price", e.target.value)} className="h-8 text-sm" data-testid={`item-price-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[11px] text-slate-500">Quantite</Label>
                          <Input type="number" min="1" value={item.quantity} onChange={e => setItem(idx, "quantity", e.target.value)} className="h-8 text-sm" data-testid={`item-qty-${idx}`} />
                        </div>
                      </div>
                    </div>
                    {form.items.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-500 shrink-0 mt-5" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}>
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-end mt-2">
                <span className="text-sm font-semibold text-slate-700">Total HT : {formatEur(totalItems)}</span>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <textarea
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[60px] resize-y"
                placeholder="Notes internes ou commentaires..."
                value={form.notes || ""}
                onChange={e => setField("notes", e.target.value)}
                data-testid="contract-notes"
              />
            </div>
            <Button onClick={save} className="w-full bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="contract-save-btn">
              {isEditing ? "Mettre à jour" : "Creer le contrat"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
